import * as React from 'react';
import TextField, { type TextFieldProps } from '@mui/material/TextField';
import type { SxProps, Theme } from '@mui/material/styles';

/** Shared `sx` for outlined MUI text fields across the app (settings, invoicing, recipes, filters, etc.). */
export const appOutlinedTextFieldSx: SxProps<Theme> = {
  '& .MuiOutlinedInput-root': {
    minHeight: 52,
    borderRadius: '8px',
    bgcolor: '#fff',
    alignItems: 'stretch',
    '&.MuiOutlinedInput-multiline': {
      minHeight: 'auto',
      alignItems: 'flex-start',
      paddingTop: '4px',
      paddingBottom: '4px',
      borderRadius: '12px',
    },
    '& fieldset': {
      borderColor: '#0000003B',
    },
    '&:hover fieldset': {
      borderColor: '#00000052',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#52B2D7',
    },
    '& input': {
      fontSize: '0.9rem',
      color: '#1A2530',
      paddingTop: '15px',
      paddingBottom: '12px',
      '&::placeholder': {
        fontFamily: 'Inter, system-ui, sans-serif',
        fontWeight: 400,
        fontStyle: 'normal',
        fontSize: '1rem',
        lineHeight: '24px',
        letterSpacing: '0.15px',
        leadingTrim: 'none',
        color: '#7B8A96',
        opacity: 1,
      },
    },
    '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
      WebkitAppearance: 'none',
      margin: 0,
    },
    '& input[type=number]': {
      MozAppearance: 'textfield',
    },
    '& textarea': {
      fontFamily: 'Inter, system-ui, sans-serif',
      fontSize: '1rem',
      fontWeight: 400,
      fontStyle: 'normal',
      lineHeight: '24px',
      letterSpacing: '0.15px',
      color: '#1A2530',
      padding: '15px 0px',
      boxSizing: 'border-box',
      resize: 'vertical',
      overflow: 'auto',
      '&::placeholder': {
        fontFamily: 'Inter, system-ui, sans-serif',
        fontWeight: 400,
        fontStyle: 'normal',
        fontSize: '1rem',
        lineHeight: '24px',
        letterSpacing: '0.15px',
        leadingTrim: 'none',
        color: '#7B8A96',
        opacity: 1,
      },
    },
    '& .MuiSelect-select': {
      fontSize: '0.9rem',
      color: '#1A2530',
      paddingTop: '13px',
      paddingBottom: '15px',
      lineHeight: 1.5,
      display: 'flex',
      alignItems: 'center',
      boxSizing: 'border-box',
    },
  },
  '& .MuiInputLabel-root': {
    fontFamily: 'Inter, system-ui, sans-serif',
    fontWeight: 400,
    fontStyle: 'normal',
    // Use 1rem base size so MUI's default shrink scale(0.75) computes the notch correctly
    // 16px * 0.75 = 12px (0.75rem) visual size when shrunk.
    fontSize: '1rem',
    // 16px * 0.75 = 12px visual line-height
    lineHeight: '16px',
    // 0.2px * 0.75 = 0.15px visual letter-spacing
    letterSpacing: '0.2px',
    verticalAlign: 'middle',
    color: '#6E7E89',
    '&.Mui-focused': {
      color: '#4D5A65',
    },
    // Spec: leading-trim: none (limited browser support; harmless when ignored)
    leadingTrim: 'none',
    '& .MuiFormLabel-asterisk': {
      color: 'inherit',
    },
  },
  // Background and padding for the break so the line doesn't show behind text
  '& .MuiInputLabel-shrink': {
    bgcolor: '#fff',
    px: '5px',
  },
  // Legend inner padding is symmetric in MUI (5+5); keep it explicit so left/right gap matches.
  '& .MuiOutlinedInput-notchedOutline legend span': {
    paddingLeft: '5px',
    paddingRight: '5px',
  },

};

function mergeAppOutlinedFieldSx(extra?: SxProps<Theme>): SxProps<Theme> {
  if (extra == null) return appOutlinedTextFieldSx;
  const parts = Array.isArray(extra) ? extra : [extra];
  return [appOutlinedTextFieldSx, ...parts] as SxProps<Theme>;
}

function mergeSlotProps(
  user: TextFieldProps['slotProps'] | undefined,
  shrinkLabel: boolean
): TextFieldProps['slotProps'] {
  const defaultInputLabel = { shrink: shrinkLabel };
  if (!user) {
    return { inputLabel: defaultInputLabel };
  }
  const { inputLabel, ...rest } = user;
  if (typeof inputLabel === 'function') {
    return user;
  }
  if (inputLabel === undefined) {
    return { ...rest, inputLabel: defaultInputLabel };
  }
  if (inputLabel && typeof inputLabel === 'object' && !Array.isArray(inputLabel)) {
    return { ...rest, inputLabel: { ...defaultInputLabel, ...inputLabel } };
  }
  return { ...rest, inputLabel };
}

export type AppOutlinedTextFieldProps = Omit<TextFieldProps, 'variant' | 'sx'> & {
  variant?: TextFieldProps['variant'];
  sx?: TextFieldProps['sx'];
  /** When true (default), the outlined label stays shrunk (floating). */
  shrinkLabel?: boolean;
};

/**
 * Outlined `TextField` with {@link appOutlinedTextFieldSx} and default `slotProps.inputLabel.shrink`.
 * Forwards all other TextField props (select, multiline, InputProps, etc.).
 */
export const AppOutlinedTextField = React.forwardRef<HTMLDivElement, AppOutlinedTextFieldProps>(
  function AppOutlinedTextField({ sx, slotProps, variant = 'outlined', shrinkLabel = true, ...rest }, ref) {
    return (
      <TextField
        ref={ref}
        variant={variant}
        sx={mergeAppOutlinedFieldSx(sx)}
        slotProps={mergeSlotProps(slotProps, shrinkLabel)}
        {...rest}
      />
    );
  }
);
